// useTypeFieldsResolvedMeta.js

import { useMemo } from 'react';

/**
 * useTypeFieldsResolvedMeta
 *
 * Resolves effective workspace/type keys and display label for type field mutations.
 *
 * @param {string} nodeTypeKey - Normalized node type key from component props.
 * @param {object|null} typeDef - Resolved node type definition.
 * @param {string|null} workspaceKey - Optional workspace key override.
 *
 * @returns {object} Resolved workspace key, type key, and display label.
 */
export default function useTypeFieldsResolvedMeta({
  nodeTypeKey,
  typeDef,
  workspaceKey,
}) {
  const resolvedWorkspaceKey = useMemo(() => {
    if (typeof workspaceKey === 'string' && workspaceKey.trim()) return workspaceKey.trim();
    if (typeof typeDef?.workspaceKey === 'string' && typeDef.workspaceKey.trim()) return typeDef.workspaceKey.trim();
    return null;
  }, [typeDef?.workspaceKey, workspaceKey]);

  const resolvedTypeKey = useMemo(() => {
    const fromDef = typeDef?.key ? String(typeDef.key).trim() : '';
    if (fromDef) return fromDef;
    return nodeTypeKey;
  }, [nodeTypeKey, typeDef?.key]);

  const nodeTypeLabel = useMemo(() => {
    const fromDef = typeof typeDef?.label === 'string' ? typeDef.label.trim() : '';
    if (fromDef) return fromDef;
    return resolvedTypeKey || 'Type';
  }, [resolvedTypeKey, typeDef?.label]);

  return {
    resolvedWorkspaceKey,
    resolvedTypeKey,
    nodeTypeLabel,
  };
}
